"use client";

import { useEffect, useRef, useState } from "react";
import classNames from "@/helpers/classNames";
import Link from "@/components/Link";
import Button from "@/components/Button";
import Text from "@/components/Text";
import Icon from "@/components/Icon";
import { PORTAL_DASHBOARD_HREF, PORTAL_NAV_LABEL } from "@/tools/portal/constants";

import styles from "./styles.module.scss";
import type { IHeaderObject } from "@/tools/sanity/schema/objects/header";

/** The panel's id, for the menu button's `aria-controls`. */
export const MOBILE_NAVIGATION_ID = "mobile-navigation";

interface HeaderNavigationMobileProps {
  className?: string;
  header?: IHeaderObject;
  open: boolean;
  hasPortalAccess?: boolean;
}

const HeaderNavigationMobile = (props: HeaderNavigationMobileProps) => {
  const { className, header, open, hasPortalAccess = false } = props || {};
  const { navItems, addButton, button, addSecondaryButton, secondaryButton } = header || {};

  // When signed in, the primary "Login" button becomes a link to the portal.
  // While `NEXT_PUBLIC_DISABLE_PORTAL` is set there is no session to be had —
  // `useSessionUser` answers false without asking — so this falls back to the
  // CMS "Login" entry on its own, with no flag check of its own.
  const primaryLabel = hasPortalAccess ? PORTAL_NAV_LABEL : button?.label;
  const primaryLinkProps = hasPortalAccess ? { href: PORTAL_DASHBOARD_HREF } : button?.link;
  const [activeSubmenu, setActiveSubmenu] = useState<number | null>(null);
  const submenuRefs = useRef<(HTMLDivElement | null)[]>([]);
  const triggerRefs = useRef<(HTMLLIElement | null)[]>([]);
  // The submenu just closed with Back, whose trigger takes focus again.
  const returnFocusTo = useRef<number | null>(null);

  // Whatever is off screen is `inert`, so focus has to move with the panel:
  // into a submenu as it opens, and back to its trigger when Back closes it.
  // Otherwise the focused button turns inert under the user and focus drops
  // to the page. `preventScroll`, because the panels slide in by transform
  // inside an `overflow: hidden` box, which focus would otherwise scroll.
  useEffect(() => {
    if (activeSubmenu !== null) {
      submenuRefs.current[activeSubmenu]?.querySelector("button")?.focus({ preventScroll: true });
      return;
    }
    if (returnFocusTo.current !== null) {
      triggerRefs.current[returnFocusTo.current]
        ?.querySelector("button")
        ?.focus({ preventScroll: true });
      returnFocusTo.current = null;
    }
  }, [activeSubmenu]);

  const closeSubmenu = () => {
    returnFocusTo.current = activeSubmenu;
    setActiveSubmenu(null);
  };
  const classes = classNames(styles.container, { [styles.open]: !!open });
  const navClasses = classNames(styles.nav, { [styles.open]: !!open }, className);

  return (
    <div className={classes} data-theme="light">
      {/* Closed, the panel only slides off screen, so its links stayed in the
          tab order and the accessibility tree. `inert` takes them out. */}
      <div id={MOBILE_NAVIGATION_ID} className={navClasses} inert={!open}>
        <ul className={styles.navItems} inert={activeSubmenu !== null}>
          {navItems?.map((item, index) => {
            const { title, link, dropdown } = item;
            return (
              <li
                key={index}
                ref={(element) => {
                  triggerRefs.current[index] = element;
                }}
                className={classNames(styles.navItem, { [styles.noDropdown]: !dropdown })}
              >
                {dropdown ? (
                  <Button
                    className={styles.navItemContent}
                    onClick={() => setActiveSubmenu(index)}
                    aria-expanded={activeSubmenu === index}
                    variant="content"
                    size="none"
                  >
                    <Text className={styles.navItemText} text={title} weight="semibold" />
                    <Icon className={styles.navItemIcon} title="triangleRight" />
                  </Button>
                ) : (
                  <Link className={styles.navItemContent} {...link} size="none" variant="content">
                    <Text className={styles.navItemText} text={title} weight="semibold" />
                  </Link>
                )}
              </li>
            );
          })}
        </ul>

        {navItems?.map((item, index) => {
          if (!item.dropdown || !item.navSublinks) {
            return null;
          }
          return (
            <div
              key={index}
              ref={(element) => {
                submenuRefs.current[index] = element;
              }}
              className={classNames(styles.submenu, {
                [styles.submenuOpen]: activeSubmenu === index,
              })}
              inert={activeSubmenu !== index}
            >
              <Button
                className={styles.submenuBack}
                onClick={closeSubmenu}
                variant="content"
                size="none"
              >
                <Icon className={styles.submenuBackIcon} title="triangleLeft" />
                <Text className={styles.submenuBackText} text={item.title} weight="semibold" />
              </Button>
              <div className={styles.explore}>
                <Text className={styles.submenuBackText} text="Explore" weight="semibold" />
                <Text text="See all" variant="label" size="md" />
              </div>
              <ul>
                {item.navSublinks.map((group, groupIndex) => (
                  <li key={groupIndex} className={styles.submenuLinksContainer}>
                    <Text
                      variant="eyebrow"
                      text={group.title}
                      className={styles.submenuGroupTitle}
                      weight="medium"
                      color={group?.titleColour?.colorKey}
                    />
                    <ul className={styles.submenuLinks}>
                      {group.links?.map((sublink, linkIndex) => (
                        <li key={linkIndex}>
                          <Link className={styles.submenuLink} variant="content" {...sublink.link}>
                            <Text text={sublink.title} weight="medium" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}

        {(addButton || addSecondaryButton) && (
          <div className={styles.buttonContainer} inert={activeSubmenu !== null}>
            {addButton && (
              <Link
                {...primaryLinkProps}
                className={styles.button}
                size="md"
                theme="yellow"
                aria-label={hasPortalAccess ? PORTAL_NAV_LABEL : undefined}
              >
                {hasPortalAccess ? (
                  <Icon title="user" className={styles.userIcon} color="themeFgDefault" />
                ) : (
                  <Text text={primaryLabel} weight="medium" />
                )}
              </Link>
            )}
            {addSecondaryButton && (
              <Link
                {...secondaryButton?.link}
                className={classNames(styles.button, styles.secondaryButton)}
                size="md"
                theme="outline-black"
              >
                <Text text={secondaryButton?.label} weight="medium" />
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HeaderNavigationMobile;
